import { Button } from "@repo/ui/components/button";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";

import { ScheduleTimelineBoard } from "@/components/schedule/schedule-timeline-board";
import { useRoleAccess } from "@/lib/role-access";

const callTimesDateRegex = /^\d{4}-\d{2}-\d{2}$/;

export const Route = createFileRoute("/_authed/call-times/$date")({
  head: () => ({ meta: [{ title: "Call times" }] }),
  component: RouteComponent,
  validateSearch: z.object({
    reviewChanges: z.coerce.boolean().optional(),
  }),
});

function parseScheduleDate(date: string) {
  return new Date(`${date}T00:00:00`);
}

function RouteComponent() {
  const { isAdmin, isPhotographer } = useRoleAccess();
  const isReadOnly = isPhotographer || !isAdmin;
  const { date } = Route.useParams();
  const navigate = useNavigate({ from: Route.fullPath });

  if (!callTimesDateRegex.test(date) || Number.isNaN(parseScheduleDate(date).getTime())) {
    return (
      <div className="flex min-h-full items-center justify-center bg-slate-950 p-6 text-slate-100">
        <div className="max-w-md rounded-lg border border-slate-800 bg-slate-900 p-6">
          <h1 className="text-lg font-semibold">Invalid call-times date</h1>
          <p className="mt-2 text-sm text-slate-400">
            Use the YYYY-MM-DD format, for example /call-times/2026-06-06.
          </p>
          <Button className="mt-4" variant="secondary" render={<Link to="/call-times" />}>
            Back to overview
          </Button>
        </div>
      </div>
    );
  }

  const selectedDate = parseScheduleDate(date);

  const handleDateChange = (nextDate: Date) => {
    navigate({
      to: "/call-times/$date",
      params: { date: nextDate.toLocaleDateString("sv-SE") },
      replace: true,
      search: {},
    });
  };

  return (
    <div className="flex h-[calc(100svh-3.5rem)] min-h-0 overflow-hidden p-3">
      <ScheduleTimelineBoard
        readOnly={isReadOnly}
        date={selectedDate}
        onDateChange={handleDateChange}
      />
    </div>
  );
}
